import React, { useEffect, useState } from 'react';
import { ID_INVITATION_PHONE_OTP_SUBMIT_BUTTON } from '../../../../autoIds';
import { autoId } from '@workday/upc-common';
import { ResendOTP } from '../../../../shared/resend-otp/resend-otp';
import { findCountryCode } from '../../../../utils/countryCodes';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { useInitUpdatePhoneMutation, useUpdatePhoneVerifyOtpMutation } from '../../../../slices/account-api.slice';
import { OTPFormField, PasscodeFormState } from '../../../../shared/otp-form-field/otp-form-field';
import { useForm } from 'react-hook-form';
import styles from './ManagePhoneNumbersPhoneOTPVerify.module.scss';
import { useTranslation } from 'react-i18next';
import { useErrorHandling } from '../../../../hooks/useErrorHandling';
import { serverError } from '../../../../utils/server-error';

export function ManagePhoneNumbersPhoneOTPVerify({ onPrevious, phoneContact, userInfo, handleDone }) {
    const [initUpdatePhone, initUpdatePhoneMutation] = useInitUpdatePhoneMutation();
    const [updatePhoneVerifyOtp, updatePhoneVerifyOtpMutation] = useUpdatePhoneVerifyOtpMutation();

    const { t } = useTranslation('account');

    const { control, setError, handleSubmit } = useForm<PasscodeFormState>({
        defaultValues: {
            passcode: ''
        }
    });

    const [phoneContactId, setPhoneContactId] = useState(phoneContact.contactId);
    const countryCode = findCountryCode(userInfo.userUnverifiedPhone?.countryCode?.shortName);
    const phoneNumber = phoneContact.phone;
    const fullPhoneNumber = countryCode.code + '******' + phoneNumber.substr(-4);

    useEffect(() => {
        if (initUpdatePhoneMutation.isSuccess) {
            setPhoneContactId(initUpdatePhoneMutation.data.contactId);
        }
    }, [initUpdatePhoneMutation]);

    useErrorHandling(initUpdatePhoneMutation.error, {
        inlineErrors: ['UPC-1006', 'UPC-1017'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    useEffect(() => {
        if (updatePhoneVerifyOtpMutation.isSuccess) {
            handleDone();
        }
    }, [handleDone, updatePhoneVerifyOtpMutation]);

    useErrorHandling(updatePhoneVerifyOtpMutation.error, {
        inlineErrors: ['UPC-1005', 'UPC-1054'],
        onInlineError: error => setError('passcode', serverError(error))
    });

    function onEditClick() {
        onPrevious();
    }

    function onSubmit({ passcode }) {
        updatePhoneVerifyOtp({ passcode, contactId: phoneContactId });
    }

    function onResend() {
        const payload = {
            countryCode: countryCode.code,
            number: phoneContact.phone,
            showToast: true
        };
        initUpdatePhone(payload);
    }

    const isLoading = updatePhoneVerifyOtpMutation.isLoading || initUpdatePhoneMutation.isLoading;

    return (
        <div className='settingsModalContent'>
            <h1 className={styles.title}>{t('ManagePhoneNumbersPhoneOTPVerify.title')}</h1>
            <h2 className={styles.body}>{t('ManagePhoneNumbersPhoneOTPVerify.description')}</h2>
            <p className={styles.phoneNumber}>{fullPhoneNumber}</p>

            <form onSubmit={handleSubmit(onSubmit)}>
                <OTPFormField autoFocus={true} disabled={isLoading} control={control} className={styles.otpField} />

                <div className='settingsModalContent__footer'>
                    <PrimaryButton
                        grow={true}
                        className={styles.button}
                        {...autoId(ID_INVITATION_PHONE_OTP_SUBMIT_BUTTON)}
                        disabled={isLoading}
                        size='large'
                        type='submit'>
                        {t('ManagePhoneNumbersPhoneOTPVerify.submitButton')}
                    </PrimaryButton>

                    <ResendOTP isDisabled={isLoading} onResend={onResend} otpType='phone' />

                    <TertiaryButton onClick={onEditClick} size='large'>
                        {t('ManagePhoneNumbersPhoneOTPVerify.backButton')}
                    </TertiaryButton>
                </div>
            </form>
        </div>
    );
}
